/**
 * Operator preference for the handoff/notification chime: whether it rings, how many times, and
 * how loud. Stored per browser (localStorage) — each operator tunes their own machine, which is
 * what "quiero que suene 2 o 3 veces" actually means in a busy kitchen vs a quiet office.
 */
export interface NotificationSoundSettings {
  enabled: boolean;
  /** How many times the chime plays per notification (1–{@link MAX_REPEATS}). */
  repeats: number;
  /**
   * Keep ringing, on a repeating cycle, for as long as ANY conversation is still waiting for a
   * human — instead of a single burst that can be missed. Stops by itself the moment the last
   * pending handoff is attended, so it can only ring while there is real work waiting.
   */
  untilAttended: boolean;
  /** 0–1. */
  volume: number;
}

const STORAGE_KEY = 'menteo:notification-sound';

export const DEFAULT_SOUND_SETTINGS: NotificationSoundSettings = {
  enabled: true,
  repeats: 1,
  untilAttended: false,
  volume: 0.6,
};

export const MAX_REPEATS = 10;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function loadSoundSettings(): NotificationSoundSettings {
  if (typeof window === 'undefined') return DEFAULT_SOUND_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SOUND_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<NotificationSoundSettings>;
    return {
      enabled: typeof parsed.enabled === 'boolean' ? parsed.enabled : DEFAULT_SOUND_SETTINGS.enabled,
      repeats: clamp(Math.round(Number(parsed.repeats) || DEFAULT_SOUND_SETTINGS.repeats), 1, MAX_REPEATS),
      untilAttended:
        typeof parsed.untilAttended === 'boolean' ? parsed.untilAttended : DEFAULT_SOUND_SETTINGS.untilAttended,
      volume: clamp(Number(parsed.volume ?? DEFAULT_SOUND_SETTINGS.volume), 0, 1),
    };
  } catch {
    return DEFAULT_SOUND_SETTINGS;
  }
}

export function saveSoundSettings(settings: NotificationSoundSettings): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage unavailable (private mode/quota) — the in-memory value still applies this session.
  }
}
